// src/components/dashboard/StaffSessionReport.jsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";

// Core Guaranteed MUI Icons
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const StaffSessionReport = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Filter States
  const [clientName, setClientName] = useState("");
  const [staff, setStaff] = useState("");
  const [quickRange, setQuickRange] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [procedureCode, setProcedureCode] = useState("");
  const [program, setProgram] = useState("All");

  // Pagination States
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Exact Dataset from Screenshot
  const [sessionRows, setSessionRows] = useState([
    { id: 1, clientName: "asad asass", numberOfSessions: 2 },
    { id: 2, clientName: "fdfdd df", numberOfSessions: 1 },
    { id: 3, clientName: "Inzimam Haq", numberOfSessions: 4 },
    { id: 4, clientName: "test pass", numberOfSessions: 1 },
  ]);

  // Unified Clinical Palette Tokens
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";

  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const tableHeaderBg = isDark ? "#162238" : "#F8FAFC";
  const rowHoverBg = isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.03)";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";

  const handleClear = () => {
    setClientName("");
    setStaff("");
    setQuickRange("");
    setDateFrom("");
    setDateTo("");
    setProcedureCode("");
    setProgram("All");
  };

  const handleSearch = () => {
    // Filter trigger logic if needed
  };

  // Filtered Rows based on Search
  const filteredRows = sessionRows.filter((r) =>
    clientName ? r.clientName.toLowerCase().includes(clientName.toLowerCase()) : true
  );

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        borderRadius: "8px",
        color: primaryTextColor,
        boxShadow: isDark ? "0 12px 32px rgba(0, 0, 0, 0.6)" : "0 8px 24px rgba(15, 23, 42, 0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.82rem",
          py: 0.8,
          "&:hover": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : pastelTealBg,
          },
          "&.Mui-selected": {
            backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg,
            color: tealButton,
            fontWeight: 700,
          },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. TOP HEADER BANNER */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            backgroundColor: tealMain,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <PeopleAltRoundedIcon sx={{ fontSize: "1.75rem" }} />
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Typography
              sx={{
                fontSize: { xs: "1.2rem", md: "1.4rem" },
                fontWeight: 800,
                color: primaryTextColor,
                letterSpacing: "-0.3px",
                lineHeight: 1.2,
              }}
            >
              Staff Sessions Report
            </Typography>
            <Chip
              label="Sessions & Encounters"
              size="small"
              sx={{
                height: "22px",
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: pastelTealBg,
                color: tealButton,
                borderRadius: "6px",
              }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: "0.82rem",
              color: secondaryTextColor,
              fontWeight: 500,
              mt: 0.3,
            }}
          >
            Track staff sessions and client interactions
          </Typography>
        </Box>
      </Box>

      {/* 2. FILTER SESSIONS SECTION */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.5, md: "26px 30px" },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <SearchIcon sx={{ color: labelTeal, fontSize: "1.3rem" }} />
          <Typography
            sx={{
              fontSize: "1.05rem",
              fontWeight: 800,
              color: primaryTextColor,
              letterSpacing: "-0.2px",
            }}
          >
            Filter Sessions
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2.5,
          }}
        >
          {/* Client Name */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>
              Client Name
            </Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem" }}>
                      Search and select patients...
                    </Typography>
                  );
                }
                return selected;
              }}
              sx={{
                borderRadius: "8px",
                backgroundColor: controlBg,
                color: primaryTextColor,
                fontSize: "0.85rem",
                "& fieldset": { borderColor: controlBorder },
                "&:hover fieldset": { borderColor: tealMain },
                "&.Mui-focused fieldset": { borderColor: tealMain },
                "& .MuiSelect-icon": { color: secondaryTextColor },
              }}
            >
              <MenuItem value="">All Patients</MenuItem>
              <MenuItem value="asad asass">asad asass</MenuItem>
              <MenuItem value="fdfdd df">fdfdd df</MenuItem>
              <MenuItem value="Inzimam Haq">Inzimam Haq</MenuItem>
              <MenuItem value="test pass">test pass</MenuItem>
            </Select>
          </Box>

          {/* Staff */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>
              Staff
            </Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={staff}
              onChange={(e) => setStaff(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem" }}>
                      Select staff...
                    </Typography>
                  );
                }
                return selected;
              }}
              sx={{
                borderRadius: "8px",
                backgroundColor: controlBg,
                color: primaryTextColor,
                fontSize: "0.85rem",
                "& fieldset": { borderColor: controlBorder },
                "&:hover fieldset": { borderColor: tealMain },
                "&.Mui-focused fieldset": { borderColor: tealMain },
                "& .MuiSelect-icon": { color: secondaryTextColor },
              }}
            >
              <MenuItem value="Dr. Usman Rehman">Dr. Usman Rehman</MenuItem>
              <MenuItem value="Dr. Sarah Jenkins">Dr. Sarah Jenkins</MenuItem>
              <MenuItem value="Elena Rostova, RN">Elena Rostova, RN</MenuItem>
              <MenuItem value="Dr. Bilal">Dr. Bilal</MenuItem>
            </Select>
          </Box>

          {/* Quick Range */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>
              Quick Range
            </Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={quickRange}
              onChange={(e) => setQuickRange(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem" }}>
                      Select date range...
                    </Typography>
                  );
                }
                return selected;
              }}
              sx={{
                borderRadius: "8px",
                backgroundColor: controlBg,
                color: primaryTextColor,
                fontSize: "0.85rem",
                "& fieldset": { borderColor: controlBorder },
                "&:hover fieldset": { borderColor: tealMain },
                "&.Mui-focused fieldset": { borderColor: tealMain },
                "& .MuiSelect-icon": { color: secondaryTextColor },
              }}
            >
              <MenuItem value="Today">Today</MenuItem>
              <MenuItem value="Yesterday">Yesterday</MenuItem>
              <MenuItem value="This Week">This Week</MenuItem>
              <MenuItem value="This Month">This Month</MenuItem>
            </Select>
          </Box>

          {/* Date of Session: from to */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>
              Date of Session:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
              <Typography sx={{ fontSize: "0.82rem", color: secondaryTextColor, fontWeight: 600 }}>
                to
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
            </Box>
          </Box>

          {/* Procedure Codes */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>
              Procedure Codes
            </Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={procedureCode}
              onChange={(e) => setProcedureCode(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem" }}>
                      Select Procedure Codes
                    </Typography>
                  );
                }
                return selected;
              }}
              sx={{
                borderRadius: "8px",
                backgroundColor: controlBg,
                color: primaryTextColor,
                fontSize: "0.85rem",
                "& fieldset": { borderColor: controlBorder },
                "&:hover fieldset": { borderColor: tealMain },
                "&.Mui-focused fieldset": { borderColor: tealMain },
                "& .MuiSelect-icon": { color: secondaryTextColor },
              }}
            >
              <MenuItem value="99214">99214 - Office Outpatient Moderate</MenuItem>
              <MenuItem value="95816">95816 - Electroencephalogram (EEG)</MenuItem>
              <MenuItem value="95910">95910 - Nerve Conduction Studies</MenuItem>
              <MenuItem value="m5">m5 - General Procedure</MenuItem>
            </Select>
          </Box>

          {/* Program */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>
              Program
            </Typography>
            <Box sx={{ position: "relative" }}>
              <Select
                fullWidth
                size="small"
                value={program}
                onChange={(e) => setProgram(e.target.value)}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuProps}
                sx={{
                  borderRadius: "8px",
                  backgroundColor: controlBg,
                  color: primaryTextColor,
                  fontSize: "0.85rem",
                  "& fieldset": { borderColor: controlBorder },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                  "& .MuiSelect-icon": { color: secondaryTextColor },
                }}
                endAdornment={
                  program !== "All" && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setProgram("All");
                      }}
                      sx={{ mr: 2, color: secondaryTextColor, p: 0.3 }}
                    >
                      <CloseRoundedIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  )
                }
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="ARMHS">ARMHS</MenuItem>
                <MenuItem value="Doula Services">Doula Services</MenuItem>
                <MenuItem value="RN Services">RN Services</MenuItem>
              </Select>
            </Box>
          </Box>
        </Box>

        {/* Buttons: Search & Clear */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1.5,
            mt: 3.5,
          }}
        >
          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={<SearchIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              height: "40px",
              backgroundColor: tealMain,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.84rem",
              borderRadius: "8px",
              px: 3,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: tealButton,
                boxShadow: "none",
              },
            }}
          >
            Search
          </Button>

          <Button
            variant="outlined"
            onClick={handleClear}
            startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              height: "40px",
              borderColor: tealButton,
              color: tealButton,
              fontWeight: 700,
              fontSize: "0.84rem",
              borderRadius: "8px",
              px: 2.8,
              textTransform: "none",
              "&:hover": {
                borderColor: tealMain,
                backgroundColor: "rgba(36, 94, 101, 0.05)",
              },
            }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      {/* 3. SESSIONS RESULTS TABLE (Exact Screenshot Replication) */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "16px",
          border: `1px solid ${softCardBorder}`,
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          overflow: "hidden",
        }}
      >
        <TableContainer
          sx={{
            maxHeight: "560px",
            overflowY: "auto",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Table stickyHeader sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: primaryTextColor,
                    fontWeight: 800,
                    fontSize: "0.86rem",
                    backgroundColor: tableHeaderBg,
                    borderBottom: `1px solid ${softCardBorder}`,
                    py: 2,
                    px: 3,
                  },
                }}
              >
                <TableCell sx={{ width: "50%" }}>Client Name</TableCell>
                <TableCell sx={{ width: "50%" }}>Number Of Sessions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} sx={{ py: 8, textAlign: "center" }}>
                    <Typography sx={{ color: secondaryTextColor, fontSize: "0.9rem", fontWeight: 600 }}>
                      No rows
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        transition: "background-color 0.15s ease",
                        "&:hover": { backgroundColor: rowHoverBg },
                        "& td": {
                          borderBottom: `1px solid ${
                            isDark ? "rgba(255, 255, 255, 0.04)" : "#F1F5F9"
                          }`,
                          py: 2.2,
                          px: 3,
                        },
                      }}
                    >
                      {/* Client Name */}
                      <TableCell sx={{ fontWeight: 600, color: primaryTextColor, fontSize: "0.88rem" }}>
                        {row.clientName}
                      </TableCell>

                      {/* Number Of Sessions */}
                      <TableCell sx={{ color: primaryTextColor, fontSize: "0.88rem", fontWeight: 600 }}>
                        {row.numberOfSessions}
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Bottom Pagination Bar */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: `1px solid ${softCardBorder}`,
            color: secondaryTextColor,
            "& .MuiTablePagination-select": {
              color: primaryTextColor,
              fontWeight: 600,
            },
            "& .MuiTablePagination-selectIcon": {
              color: secondaryTextColor,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default StaffSessionReport;